/**
 * IDoScreen - K Subitizing I Do Practice
 *
 * Revamped to match PreK structure with shared components and proper cleanup.
 * K-specific: quantities 1-6, K audio files for 5-6.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { NUMERAL_FONT_STYLE } from '../../../styles/design-system/typography';
import { KPracticeSettings } from '../types';
import { DotPattern } from './types';
import { useKTTS } from '../../../hooks/useKAudio';
import { TRACK_RANGES } from '../../../types/constants';

// Use PreK shared components (they're generic enough for K)
import { PracticeHeader } from '../../PreKPractice/shared';
// Use K-specific constants for timing
import {
  PRACTICE_PHASES,
  K_PRACTICE_TIMER_CONSTANTS,
  PRACTICE_VISUAL_CONFIG,
} from '../shared';

// ============================================================================
// K-Specific Constants
// ============================================================================

const K_TRACK_ID = 'TRACK_K_SUBITIZING';

/**
 * Convert K subitizing fact ID to quantity value
 * K subitizing facts: FACT1619-FACT1624 map to quantities 1-6
 */
const factIdToQuantity = (factId: string): number => {
  const factNumber = parseInt(factId.replace('FACT', ''));
  const [startFact] = TRACK_RANGES.TRACK_K_SUBITIZING;
  return factNumber - startFact + 1;
};

interface IDoScreenProps {
  settings: KPracticeSettings;
  factsToPractice: string[];
  onComplete: () => void;
  onBack: () => void;
}

const IDoScreen: React.FC<IDoScreenProps> = ({
  settings,
  factsToPractice,
  onComplete,
  onBack,
}) => {
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState<'showing' | 'flashing' | 'blank' | 'waiting'>(
    'showing'
  );
  const [showDots, setShowDots] = useState(false);
  const [currentPattern, setCurrentPattern] = useState<DotPattern | null>(null);
  const [autoAdvanceTimer, setAutoAdvanceTimer] = useState<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Ref to store timeout IDs for cleanup
  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);

  const { playTTSWithCallback, isTTSPlaying } = useKTTS();

  // Generate dot pattern using 3x3 grid (same grid works for K quantities 1-6)
  const generateDotPattern = useCallback((quantity: number): DotPattern => {
    const gridPositions = PRACTICE_VISUAL_CONFIG.SUBITIZING_GRID;

    // Shuffle positions and select the first 'quantity' positions
    const shuffledPositions = [...gridPositions].sort(() => Math.random() - 0.5);
    const selectedPositions = shuffledPositions.slice(0, quantity);

    // Create dots from selected positions
    const dots = selectedPositions.map((pos, i) => ({
      x: pos.x,
      y: pos.y,
      id: `dot-${i}`,
    }));

    return {
      id: `pattern-${Date.now()}-${Math.random()}`,
      quantity,
      dots,
    };
  }, []);

  const currentFactId = factsToPractice[currentFactIndex];
  const currentDigit = currentFactId ? factIdToQuantity(currentFactId) : undefined;

  // Generate new pattern and reset phase when fact changes (combined to avoid race condition)
  useEffect(() => {
    // Guard: Don't proceed if no digit is available
    if (currentDigit === undefined) {
      return;
    }

    // Clear any pending timeouts from previous fact
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];

    setCurrentPattern(generateDotPattern(currentDigit));
    setShowDots(false);
    setCurrentPhase('showing');
  }, [currentDigit, currentFactIndex, generateDotPattern]);

  // Auto-advance to next fact after showing sequence
  const startAutoAdvance = useCallback(() => {
    if (autoAdvanceTimer) {
      clearTimeout(autoAdvanceTimer);
    }

    const timer = setTimeout(() => {
      if (currentFactIndex < factsToPractice.length - 1) {
        // Move to next fact - combined effect will handle pattern + phase reset
        setCurrentFactIndex(currentFactIndex + 1);
      } else {
        // All facts completed, move to next screen
        onComplete();
      }
    }, 1000);

    setAutoAdvanceTimer(timer);
  }, [currentFactIndex, factsToPractice.length, onComplete, autoAdvanceTimer]);

  // Start the subitizing sequence when in showing phase
  useEffect(() => {
    if (currentPhase === 'showing' && currentPattern && currentDigit !== undefined) {
      // Small delay then start flash
      const timeout1 = setTimeout(() => {
        setCurrentPhase('flashing');
        setShowDots(true);

        // Flash for configured duration
        const timeout2 = setTimeout(() => {
          setShowDots(false);
          setCurrentPhase('blank');

          // Brief blank period, then show number and play TTS
          const timeout3 = setTimeout(() => {
            // Show number card immediately when audio starts
            setCurrentPhase('waiting');

            if (settings.speechEnabled) {
              // Use PreK audio for quantities 1-4, K audio for quantities 5-6
              const audioFile = {
                filename:
                  currentDigit <= 4
                    ? `preKPractice/subitizing/iDo/prek_subitizing_ido_${currentDigit}.mp3`
                    : `kPractice/subitizing/k_subitizing_ido_${currentDigit}.mp3`,
                text: `I see ${currentDigit} dot${currentDigit === 1 ? '' : 's'}! Say ${currentDigit}!`,
              };

              // Play "I see [quantity] dots! Say [quantity]!" instruction
              playTTSWithCallback(audioFile, () => {
                // Audio finished, start auto-advance timer
                startAutoAdvance();
              }).catch(() => {
                console.error('IDoScreen subitizing TTS failed for quantity:', currentDigit);
                startAutoAdvance();
              });
            } else {
              startAutoAdvance();
            }
          }, 100);
          timeoutsRef.current.push(timeout3);
        }, K_PRACTICE_TIMER_CONSTANTS.SUBITIZING_FLASH_MS);
        timeoutsRef.current.push(timeout2);
      }, 500);
      timeoutsRef.current.push(timeout1);
    }
  }, [
    currentPhase,
    currentPattern,
    settings.speechEnabled,
    playTTSWithCallback,
    startAutoAdvance,
    currentDigit,
  ]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoAdvanceTimer) {
        clearTimeout(autoAdvanceTimer);
      }
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, [autoAdvanceTimer]);

  // Loading state: show loading screen if no digit is available
  if (currentDigit === undefined || !currentPattern) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex flex-col">
      <PracticeHeader
        phase={PRACTICE_PHASES.I_DO}
        onBack={onBack}
        progress={
          factsToPractice.length > 0
            ? Math.min((currentFactIndex + 1) / factsToPractice.length, 1)
            : 0
        }
      />

      {/* Main Content Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-6">
        <div className="w-full max-w-md lg:max-w-lg">
          {/* Showing Phase - Get Ready */}
          {currentPhase === 'showing' && (
            <div className="text-center">
              <div className="text-2xl lg:text-3xl text-gray-600 animate-pulse mb-2">
                Get ready...
              </div>
              <div className="text-lg text-gray-500">Watch carefully!</div>
            </div>
          )}

          {/* Dots Container - Uses the exact same area as the 4 option cards */}
          {(currentPhase === 'flashing' || currentPhase === 'blank') && (
            <div className="grid grid-cols-2 gap-8 lg:gap-12 relative">
              {/* Create the exact same 4-card layout area */}
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>
              <div className="h-32 lg:h-40 rounded-3xl font-bold text-5xl lg:text-6xl shadow-xl border-4 opacity-0"></div>

              {/* Dots positioned absolutely over this area - 3x3 grid positions */}
              {showDots &&
                currentPattern.dots.map((dot) => (
                  <motion.div
                    key={dot.id}
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full shadow-lg"
                    style={{
                      left: `${dot.x}%`,
                      top: `${dot.y}%`,
                      transform: 'translate(-50%, -50%)',
                    }}
                  />
                ))}
            </div>
          )}

          {/* Waiting Phase - Show large number (simplified animation like NumberNaming) */}
          {currentPhase === 'waiting' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{
                opacity: 1,
                scale: 1,
              }}
              transition={{
                duration: 0.3,
              }}
              className="w-80 h-80 bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl border-4 border-blue-700 mx-auto"
              style={NUMERAL_FONT_STYLE}
            >
              <span className="text-9xl font-bold text-white">{currentDigit}</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* Audio Playing Indicator */}
      {isTTSPlaying && (
        <div className="flex justify-center pb-8">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center text-gray-600"
          >
            <div className="animate-pulse mr-2">🔊</div>
            <span>Listen carefully...</span>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default IDoScreen;
